import * as THREE from 'three';
import type { HeaterType, SaunaDimensions } from '../../engines/types';
import { emitter } from '../../lib/eventBus';
import { resolveWallHeightM } from '../../domain/geometry/buildingDimensions';
import {
  getPartitionSplitBounds,
  type PartitionBoundsOptions,
} from '../../domain/partition/partitionSplit';
import type { PartitionMeasurementPreview } from './viewerMeasurements';

interface PartitionDragControllerConfig {
  scene: THREE.Scene;
  /**
   * Object the ghost divider is parented to, and the space the drag is
   * measured in. The viewer passes the rotated sauna rig so the split still
   * runs along the building's own length axis at a rotated orientation.
   * Falls back to the scene root (identity transform).
   */
  parent?: THREE.Object3D;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  dimensions: SaunaDimensions;
  partitionSplit: number;
  heaterType?: HeaterType;
  heaterModelId?: string | null;
  /** The design's wall height (issue 155); absent = the rule default. */
  wallHeightM?: number;
  onCommit: (partitionSplit: number) => void;
  onCancel?: () => void;
  onDragUpdate?: (valid: boolean) => void;
  onPreviewChange?: (preview: PartitionMeasurementPreview | null) => void;
}

const WALL_BASE_Y = 0.165;
const DIVIDER_DEPTH_M = 0.05;

export class PartitionDragController {
  private parent: THREE.Object3D;
  private camera: THREE.PerspectiveCamera;
  private domElement: HTMLElement;
  private dimensions: SaunaDimensions;
  private onCommit: (partitionSplit: number) => void;
  private onCancel?: () => void;
  private onDragUpdate?: (valid: boolean) => void;
  private onPreviewChange?: (preview: PartitionMeasurementPreview | null) => void;
  private currentSplit: number;
  private wallHeightM: number;
  private currentValid = true;
  private boundsOptions: PartitionBoundsOptions;
  private ghostMesh: THREE.Mesh | null = null;
  private movePlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(WALL_BASE_Y + 1.1));
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();

  private boundPointerMove: (e: PointerEvent) => void;
  private boundPointerUp: (e: PointerEvent) => void;
  private boundKeyDown: (e: KeyboardEvent) => void;

  constructor(config: PartitionDragControllerConfig) {
    this.parent = config.parent ?? config.scene;
    this.camera = config.camera;
    this.domElement = config.renderer.domElement;
    this.dimensions = config.dimensions;
    this.currentSplit = config.partitionSplit;
    this.wallHeightM = resolveWallHeightM(config.wallHeightM);
    this.onCommit = config.onCommit;
    this.onCancel = config.onCancel;
    this.onDragUpdate = config.onDragUpdate;
    this.onPreviewChange = config.onPreviewChange;
    this.boundsOptions = config.heaterType
      ? { heaterType: config.heaterType, heaterModelId: config.heaterModelId }
      : {};

    this.boundPointerMove = this.handlePointerMove.bind(this);
    this.boundPointerUp = this.handlePointerUp.bind(this);
    this.boundKeyDown = this.handleKeyDown.bind(this);
  }

  startDrag() {
    const ghost = new THREE.Mesh(
      new THREE.BoxGeometry(
        Math.max(0.6, this.dimensions.width - 0.19),
        this.wallHeightM,
        DIVIDER_DEPTH_M,
      ),
      new THREE.MeshBasicMaterial({
        color: 0x22c55e,
        transparent: true,
        opacity: 0.2,
        depthTest: false,
      }),
    );
    ghost.position.set(0, WALL_BASE_Y + (this.wallHeightM / 2), this.getDividerZ(this.currentSplit));
    ghost.name = 'detail/interaction/partition-ghost';
    this.parent.add(ghost);
    this.ghostMesh = ghost;
    this.emitPreview(true);

    this.domElement.addEventListener('pointermove', this.boundPointerMove);
    this.domElement.addEventListener('pointerup', this.boundPointerUp);
    window.addEventListener('keydown', this.boundKeyDown);
    this.domElement.style.cursor = 'ns-resize';
  }

  cancelDrag() {
    this.cleanup();
    this.onCancel?.();
    emitter.emit('drag:cancel');
  }

  dispose() {
    this.cleanup();
  }

  private handlePointerMove(event: PointerEvent) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const point = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.movePlane, point)) {
      this.currentValid = false;
      this.onDragUpdate?.(false);
      this.emitPreview(false);
      return;
    }

    // The plane is horizontal so it survives a Y rotation, but `point` is in
    // world space — the split runs along the BUILDING's length axis, so the
    // hit has to come back through the rig before its z means anything.
    this.parent.worldToLocal(point);
    const rawSplit = (point.z + (this.dimensions.length / 2)) / this.dimensions.length;
    const { min, max } = getPartitionSplitBounds(this.dimensions.length, this.boundsOptions);
    const clamped = Math.min(max, Math.max(min, rawSplit));
    this.currentValid = true;
    this.currentSplit = Number(clamped.toFixed(4));

    if (this.ghostMesh) {
      this.ghostMesh.position.z = this.getDividerZ(this.currentSplit);
      const material = this.ghostMesh.material as THREE.MeshBasicMaterial;
      material.color.setHex(0x22c55e);
    }

    this.onDragUpdate?.(true);
    this.emitPreview(true);
  }

  private handlePointerUp() {
    if (!this.currentValid) {
      this.cancelDrag();
      return;
    }

    this.onCommit(this.currentSplit);
    emitter.emit('drag:end', {
      placed: true,
      wall: 'divider',
      xMm: Math.round(this.currentSplit * this.dimensions.length * 1000),
      yMm: 0,
    });
    this.cleanup();
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.cancelDrag();
    }
  }

  private cleanup() {
    this.domElement.removeEventListener('pointermove', this.boundPointerMove);
    this.domElement.removeEventListener('pointerup', this.boundPointerUp);
    window.removeEventListener('keydown', this.boundKeyDown);
    this.domElement.style.cursor = '';
    this.onPreviewChange?.(null);

    if (this.ghostMesh) {
      this.ghostMesh.removeFromParent();
      this.ghostMesh.geometry.dispose();
      (this.ghostMesh.material as THREE.Material).dispose();
      this.ghostMesh = null;
    }
  }

  private getDividerZ(partitionSplit: number) {
    const { min, max } = getPartitionSplitBounds(this.dimensions.length, this.boundsOptions);
    const clamped = Math.min(max, Math.max(min, partitionSplit));
    return -this.dimensions.length / 2 + this.dimensions.length * clamped;
  }

  private emitPreview(valid: boolean) {
    this.onPreviewChange?.({
      partitionSplit: this.currentSplit,
      saunaDepthMm: Math.round(this.currentSplit * this.dimensions.length * 1000),
      loungeDepthMm: Math.max(0, Math.round((1 - this.currentSplit) * this.dimensions.length * 1000)),
      dividerZ: this.getDividerZ(this.currentSplit),
      valid,
    });
  }
}
